import React, { useEffect, useRef, useState } from 'react';
import { ArrowRightIcon, ArrowUpRightIcon, EyeIcon, EyeSlashIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import LoginShell from '../components/LoginShell';
import { useAuth } from '../contexts/AuthContext';
import { loginReturnTarget } from '../services/loginReturnTarget';
import { authenticatedLandingPath } from '../config/researchLanding';
import { leseLoeschAntwort, vergissLoeschAntwort } from '../services/loeschBestaetigung';
import { ANMELDUNG_NICHT_MOEGLICH, meldungOhneTitel, pruefeAnmeldeFelder } from './anmeldeMeldung';
// Profil-Nachbau F-06 (TEIL-3 SP3-05, PF-311-1): Hinweis, wenn die Anmeldung gestört ist.
import AnmeldeStoerung from '../components/hilfe/AnmeldeStoerung';
// Profil-Nachbau F-21 (F21-5, PF-332): „Problem bei der Anmeldung?“ und die Bestätigung über den Link aus der Mail.
import AnmeldeProblem from '../components/hilfe/AnmeldeProblem';
import AnmeldeProblemBestaetigt from '../components/hilfe/AnmeldeProblemBestaetigt';
import { ohneToken, TOKEN_PARAMETER, tokenAus } from '../components/hilfe/anmeldeProblemLogik';
import { ANMELDEPROBLEM_TEXTE } from '../components/hilfe/hilfeTexte';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const [error, setError] = useState('');
  // PF-LG-nach-1 (Entwurf :164-180): der Satz unter dem Feld, leer = kein Fehler.
  const [feldFehler, setFeldFehler] = useState({ email: '', passwort: '' });
  const emailFeld = useRef<HTMLInputElement>(null);
  const passwortFeld = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  // Paket AA (Befund 6): nach dem Passwort der Codeschritt, wenn das Konto Zwei-Faktor eingerichtet hat.
  // PF-332: „problem“ ist die Ansicht „Problem bei der Anmeldung?“ (keine eigene Route).
  const [schritt, setSchritt] = useState<'passwort' | 'code' | 'problem'>('passwort');
  const [code, setCode] = useState('');
  const [mitWiederherstellung, setMitWiederherstellung] = useState(false);
  const { login, loginZweiFaktor, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const returnTarget = loginReturnTarget(location.state, searchParams.get('returnTo'));
  // Nach „Daten löschen“ (?deleted=true): die Antwort des Servers, einmal; danach bleibt nichts im Speicher des Tabs.
  const [geloescht] = useState(() => searchParams.get('deleted') ? leseLoeschAntwort() : '');
  useEffect(() => { vergissLoeschAntwort(); }, []);
  // PF-332: der Token aus dem Link der Mail (?anmeldeproblem=…), beim ersten Rendern gelesen und gleich aus der Adresse genommen,
  // damit er weder im Verlauf noch in einem Referrer bleibt. Bestätigt wird erst mit dem Klick in AnmeldeProblemBestaetigt.
  const [bestaetigung, setBestaetigung] = useState<string | null>(() => tokenAus(location.search));
  useEffect(() => {
    if (new URLSearchParams(location.search).has(TOKEN_PARAMETER)) navigate({ pathname: location.pathname, search: ohneToken(location.search) }, { replace: true, state: location.state });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // PF-332: solange die Bestätigung offen ist, auch angemeldet nicht weiterleiten (der Link soll angemeldet bestätigt werden können).
    // UI-05/UI-14: ohne gespeichertes Ziel beginnt jedes Konto auf der Startseite (config/researchLanding.ts).
    if (isAuthenticated && bestaetigung === null) {
      navigate(authenticatedLandingPath(returnTarget), { replace: true });
    }
  }, [isAuthenticated, navigate, returnTarget, bestaetigung]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (schritt === 'passwort') {
      // PF-LG-nach-1 (Entwurf :273-283): erst die Felder prüfen; mit einem Fehler geht nichts an den Server, der Fokus springt ins erste
      // fehlerhafte Feld.
      const fehler = pruefeAnmeldeFelder(email, emailFeld.current?.validity.valid ?? true, password);
      setFeldFehler(fehler);
      if (fehler.email || fehler.passwort) { (fehler.email ? emailFeld : passwortFeld).current?.focus(); return; }
    }
    setLoading(true);
    setError('');
    try {
      if (schritt === 'passwort') {
        if (await login(email, password) === 'zweiFaktor') { setSchritt('code'); setPassword(''); }
      } else {
        await loginZweiFaktor(mitWiederherstellung ? { wiederherstellungscode: code } : { code: code.replace(/\s+/g, '') });
      }
    } catch (err: any) {
      // Die Anmeldeaufgabe gilt fünf Minuten; ist sie abgelaufen, hilft nur die Anmeldung mit dem Passwort.
      if (schritt === 'code' && err.response?.data?.code === 'LOGIN_CHALLENGE_INVALID') zurueckZurAnmeldung();
      setError(err.response?.data?.error || 'Anmeldung fehlgeschlagen. Bitte prüfen Sie Ihre Zugangsdaten.');
    } finally {
      setLoading(false);
    }
  };

  const zurueckZurAnmeldung = () => { setSchritt('passwort'); setCode(''); setMitWiederherstellung(false); setError(''); };
  const umschalten = () => { setMitWiederherstellung(an => !an); setCode(''); setError(''); };

  const status = searchParams.get('verified')
    ? { kind: 'success', title: 'E-Mail-Adresse bestätigt.', text: 'Sie können sich jetzt anmelden.' }
    : searchParams.get('deleted')
      ? { kind: 'success', title: 'Konto gelöscht.', text: geloescht }
    : searchParams.get('passwordChanged')
      ? { kind: 'success', title: 'Passwort geändert.', text: 'Sie wurden auf allen Geräten abgemeldet. Bitte melden Sie sich mit dem neuen Passwort an.' }
      : searchParams.get('seatChanged')
      ? { kind: 'success', title: 'Lizenzplatz geändert.', text: 'Ihre Sitzungen wurden dabei beendet. Bitte melden Sie sich erneut an.' }
      : searchParams.get('expired')
        ? { kind: 'notice', title: 'Bitte erneut anmelden.', text: 'Ihre Sitzung ist abgelaufen.' }
        : searchParams.get('reset')
          ? { kind: 'success', title: 'Passwort geändert.', text: 'Melden Sie sich mit Ihrem neuen Passwort an.' }
          : null;

  if (bestaetigung !== null) {
    return <LoginShell><AnmeldeProblemBestaetigt token={bestaetigung} zurAnmeldung={() => setBestaetigung(null)}
      erneutMelden={() => { setBestaetigung(null); setSchritt('problem'); }} /></LoginShell>;
  }
  if (schritt === 'problem') return <LoginShell><AnmeldeProblem email={email} zurueck={() => setSchritt('passwort')} /></LoginShell>;

  return (
    <LoginShell>
      {/* PF-LG-nach-2: der Codeschritt ohne login-signin, damit „Bestätigungscode“ die 40 px der übrigen Seiten der Hülle bekommt
          (mit der Entwurfsgröße von „Ihr Zugang.“ ragte das Wort bei 390 px aus der Spalte). */}
      <div className={schritt === 'code' ? 'login-form-wrap' : 'login-form-wrap login-signin'}>
        {schritt === 'code' ? <>
          <h1 id="auth-title">Bestätigungscode</h1>
          <p className="login-intro">{mitWiederherstellung ? 'Geben Sie einen Ihrer Wiederherstellungscodes ein.' : 'Geben Sie den sechsstelligen Code aus Ihrer Authenticator-App ein.'}</p>
        </> : <>
          <p className="login-kicker">Recherche im Gesetz</p>
          <h1 id="auth-title">Ihr Zugang.</h1>
          <p className="login-intro">Willkommen bei <span translate="no">CLAiRIS</span>.<br />Melden Sie sich an und sehen Sie klarer.</p>
          {status && <div className={`login-notice ${status.kind}`} role="status"><strong>{status.title}</strong><span>{status.text}</span></div>}
          <AnmeldeStoerung />
        </>}
        <form onSubmit={handleSubmit} noValidate={schritt === 'passwort'} className="login-form" aria-label="Bei CLAiRIS anmelden" aria-busy={loading} aria-describedby={error ? 'login-error' : undefined}>
          {schritt === 'code' ? <div className="login-field">
            <div className="login-label-row">
              <label htmlFor="code">{mitWiederherstellung ? 'Wiederherstellungscode' : 'Code aus der App'}</label>
              <button type="button" className="login-recovery" onClick={umschalten}>{mitWiederherstellung ? 'Code aus der App verwenden' : 'Wiederherstellungscode verwenden'}</button>
            </div>
            <input id="code" name="code" value={code} onChange={(e) => setCode(e.target.value)} required autoComplete="one-time-code" autoFocus
              inputMode={mitWiederherstellung ? 'text' : 'numeric'} autoCapitalize="characters" spellCheck={false} />
          </div> : <>
          <div className="login-field">
            <label htmlFor="email">E-Mail-Adresse</label>
            <input ref={emailFeld} id="email" name="email" type="email" inputMode="email" value={email} onChange={(e) => { setEmail(e.target.value); if (feldFehler.email) setFeldFehler((f) => ({ ...f, email: '' })); }} placeholder="name@unternehmen.at" required autoComplete="email" autoCapitalize="none" spellCheck={false}
              aria-invalid={feldFehler.email ? true : undefined} aria-describedby={feldFehler.email ? 'login-email-error' : undefined} />
            <p id="login-email-error" className="login-input-hint login-feld-fehler" aria-live="polite" hidden={!feldFehler.email}>{feldFehler.email}</p>
          </div>
          <div className="login-field">
            <div className="login-label-row">
              <label htmlFor="password">Passwort</label>
              <Link to="/forgot-password" className="login-recovery">Passwort vergessen?</Link>
            </div>
            <div className="login-password">
              <input ref={passwortFeld} id="password" name="password" type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => { setPassword(e.target.value); if (feldFehler.passwort) setFeldFehler((f) => ({ ...f, passwort: '' })); }} onKeyDown={(event) => setCapsLock(event.getModifierState('CapsLock'))} onKeyUp={(event) => setCapsLock(event.getModifierState('CapsLock'))} onBlur={() => setCapsLock(false)}
                aria-invalid={feldFehler.passwort ? true : undefined} aria-describedby={[feldFehler.passwort && 'login-password-error', capsLock && 'login-caps-lock'].filter(Boolean).join(' ') || undefined} placeholder="Ihr Passwort" required autoComplete="current-password" />
              <button type="button" onClick={() => setShowPassword((visible) => !visible)} aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'} aria-controls="password" aria-pressed={showPassword}>{showPassword ? <EyeSlashIcon aria-hidden="true" /> : <EyeIcon aria-hidden="true" />}</button>
            </div>
            <p id="login-password-error" className="login-input-hint login-feld-fehler" aria-live="polite" hidden={!feldFehler.passwort}>{feldFehler.passwort}</p>
            {capsLock && <p id="login-caps-lock" className="login-input-hint" role="status">Die Feststelltaste ist aktiviert.</p>}
          </div>
          </>}
          {error && <div id="login-error" className="login-notice error" role="alert"><strong>{ANMELDUNG_NICHT_MOEGLICH}</strong><span>{meldungOhneTitel(error)}</span></div>}
          <button type="submit" disabled={loading} className="login-submit"><span>{loading ? 'Anmeldung läuft …' : schritt === 'code' ? 'Bestätigen' : 'Anmelden'}</span>{loading ? <span className="login-state-indicator" aria-hidden="true" /> : <ArrowRightIcon aria-hidden="true" />}</button>
        </form>
        {schritt === 'code'
          ? <p className="login-access"><button type="button" className="login-recovery" onClick={zurueckZurAnmeldung}>Zurück zur Anmeldung</button></p>
          : <>
            {/* PF-LG-anmeldung-1 (Entwurf 20.10.2026): mittig „Noch keinen Zugang?“, nach einer feinen Linie „Hilfe bei der Anmeldung“
                statt „Problem bei der Anmeldung?“; der Knopf öffnet dieselbe Ansicht „problem“ wie bisher. */}
            <p className="login-access login-register">Noch keinen Zugang? <Link to="/registrieren">Jetzt registrieren<ArrowUpRightIcon aria-hidden="true" /></Link></p>
            <p className="login-help"><button type="button" className="login-help-button" onClick={() => setSchritt('problem')}><QuestionMarkCircleIcon aria-hidden="true" />{ANMELDEPROBLEM_TEXTE.link}</button></p>
          </>}
      </div>
    </LoginShell>
  );
}

export default Login;
